import { Button, ChipMetadata, LinkButton, Modal } from '@pmi/dsm-react';
import { useEffect, useState } from 'react';
import {
  CommentsOutlineIcon,
  DotSolidIcon,
} from '@pmi/dsm-react/dist/components/icons';
import { Spinner } from 'react-bootstrap';
import styles from '../../../../shared/components/InviteWizard/wizard.module.scss';
import commentStyle from './agreementComments.module.scss';
import { AgreementApiModel } from '../../../../shared/models/DealModel';
import {
  useDeleteCommentMutation,
  useGetCommentsByAccountQuery,
  usePostCommentMutation,
  useUpdateCommentMutation,
} from './apiSlice';
import { CommentItem } from '../../../../shared/models/CommentItem';
import CommentsList from './CommentsList';
import usePermissions from '../../../../shared/hooks/usePermissions';
import PERMISSIONS from '../../../../shared/permissions';
import useUser from '../../../../shared/hooks/useUser';
import { toastWithAnalytics } from '../../../../shared/analytics';

export interface AgreementCommentsProps {
  deal: AgreementApiModel;
  accountId: string;
}

export default function AgreementComments({
  deal,
  accountId,
}: AgreementCommentsProps) {
  const [animationClassName, setAnimationClassName] = useState('hidden');
  const [commentCount, setCommentCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [commentInputValue, setCommentInputValue] = useState<string>('');
  const [editCommentSelected, setEditCommentSelected] =
    useState<CommentItem | null>(null);
  const [deleteCommentSelected, setDeleteCommentSelected] =
    useState<CommentItem | null>(null);
  const [showFormButtons, setShowFormButtons] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [isPosting, setIsPosting] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const maxCharCount = 250;
  const user = useUser();
  const { hasAccess } = usePermissions();
  const isInternalAdmin = hasAccess({
    permissions: [PERMISSIONS.ManageElevatedAdmins],
  });
  const [postComment, postedComment] = usePostCommentMutation();
  const [updateComment, updatedComment] = useUpdateCommentMutation();
  const [deleteComment, deletedComment] = useDeleteCommentMutation();
  const [modalBodyDom, setModalBodyDom] = useState<HTMLDivElement>(undefined);
  const {
    data: accountCommentsData,
    isLoading: isLoadingComments,
    isFetching: isFetchingComments,
  } = useGetCommentsByAccountQuery(
    { accountId },
    {
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const reInit = () => {
    setCharCount(0);
    setCommentInputValue('');
    setShowFormButtons(false);
    setIsPosting(false);
  };

  const onPostCommentClick = () => {
    setIsPosting(true);
    if (commentInputValue.length > 0) {
      let request: any = null;
      if (editCommentSelected) {
        request = updateComment({
          commentId: editCommentSelected.id,
          payload: { description: commentInputValue },
        }).unwrap();
      } else {
        request = postComment({
          dealId: deal.id,
          accountId,
          payload: { description: commentInputValue },
        }).unwrap();
      }

      if (request) {
        request
          .then(() => {
            toastWithAnalytics(
              `Comment has been ${
                editCommentSelected ? 'updated' : 'added'
              } to agreement`,
              'success',
            );
            // reset states on success
            setEditCommentSelected(null);

            updatedComment.reset();
            postedComment.reset();
            setTimeout(() => {
              reInit();
              setIsPosting(false);
            }, 300);
          })
          .catch(() => {
            toastWithAnalytics(
              `Comment could not be ${
                editCommentSelected ? 'updated' : 'added'
              } to agreement.`,
              'danger',
            );
            // reset states on error
            setEditCommentSelected(null);

            updatedComment.reset();
            postedComment.reset();
            setIsPosting(false);
          });
      }
    }
  };

  useEffect(() => {
    setEditCommentSelected(null);
    setHasNew(false);
    reInit();
  }, [deal]);

  useEffect(() => {
    setCharCount(commentInputValue.length > 0 ? commentInputValue.length : 0);
  }, [commentInputValue]);

  useEffect(() => {
    if (editCommentSelected) {
      setCommentInputValue(editCommentSelected.description);
      setShowFormButtons(true);
    } else {
      setCommentInputValue('');
      reInit();
    }
    const container = document.querySelector('.comments-modal .modal-body');
    if (container) {
      const txtArea: HTMLTextAreaElement =
        document.querySelector('.txt-comment');
      container.scroll({ behavior: 'smooth', top: 0 });
      if (txtArea) {
        txtArea.focus();
      }
    }
  }, [editCommentSelected]);

  useEffect(() => {
    if (modalBodyDom) {
      const commentFormDiv = (): HTMLDivElement =>
        modalBodyDom.querySelector('.comment-form') as HTMLDivElement;
      if (commentFormDiv()) {
        const containerInner = (): HTMLDivElement =>
          modalBodyDom.querySelector('.comment-container') as HTMLDivElement;
        const txtArea: HTMLTextAreaElement =
          document.querySelector('.txt-comment');
        const onScroll = (e: Event) => {
          const div: HTMLDivElement = e.target as HTMLDivElement;
          const base = commentFormDiv().getBoundingClientRect().height;
          const currentTop = div.scrollTop;
          if (currentTop > base) {
            commentFormDiv().style.top = `${currentTop - 88}px`;
            commentFormDiv().classList.add('shadow-lg');
          } else {
            commentFormDiv().style.top = `0px`;
            commentFormDiv().classList.remove('shadow-lg');
          }
        };
        containerInner().style.paddingTop = `${
          commentFormDiv().getBoundingClientRect().height +
          (showFormButtons ? 40 : 30)
        }px`;

        if (txtArea) {
          const onResize = () => {
            containerInner().style.paddingTop = `${
              commentFormDiv().getBoundingClientRect().height +
              (showFormButtons ? 40 : 30)
            }px`;
          };
          txtArea.removeEventListener('resizes', onResize);
          txtArea.addEventListener('resizes', onResize);
        }

        modalBodyDom.removeEventListener('scroll', onScroll);
        modalBodyDom.addEventListener('scroll', onScroll);
      }
    }
  }, [modalBodyDom, showFormButtons]);
  useEffect(() => {
    if (deleteCommentSelected) {
      deleteComment({ commentId: deleteCommentSelected.id })
        .unwrap()
        .then(() => {
          toastWithAnalytics(
            'Comment has been deleted from agreement',
            'success',
          );
          deletedComment.reset();
        })
        .catch(() => {
          toastWithAnalytics(
            'Comment was not deleted from agreement.',
            'danger',
          );
          deletedComment.reset();
        });
    }
  }, [deleteCommentSelected]);

  useEffect(() => {
    if (!isLoadingComments) {
      if (accountCommentsData && accountCommentsData.items) {
        const temp: CommentItem[] = accountCommentsData.items.filter(
          (item: CommentItem) => item.dealId === deal.id,
        );
        const currentPersonId = user?.person_id?.toString();
        setHasNew(
          currentPersonId
            ? temp.some(
                (c) => !c.isRead && c.personId?.toString() !== currentPersonId,
              )
            : false,
        );
        setCommentCount(temp.length);
      } else {
        setCommentCount(0);
        setHasNew(false);
      }
    } else {
      setCommentCount(0);
      setHasNew(false);
    }
  }, [isLoadingComments, isFetchingComments, user]);
  useEffect(() => {
    if (showModal) {
      setTimeout(() => {
        const container = document.querySelector('.comments-modal .modal-body');
        if (container) {
          container.classList.add('comment-modal-body');
          container.classList.add(commentStyle.commentModalBody);
        }
      }, 100);
    }
  }, [showModal]);

  return (
    <>
      <div
        className={`flex align-items-center align-content-center ${commentStyle.dealCommentBlock}`}
      >
        <span className={commentStyle.commentsNewChip}>
          {hasNew && isInternalAdmin && (
            <ChipMetadata
              className="flex flex-row-reverse"
              icon={<DotSolidIcon />}
              text="New"
              variant="tangerine"
            />
          )}
        </span>
        <span className="d-sm-none d-block">
          {commentCount} {commentCount > 1 ? 'Comments' : 'Comment'}
        </span>
        <span className={`${commentStyle.commentsCounter} d-sm-block d-none`}>
          {commentCount} {commentCount > 1 ? 'Comments' : 'Comment'}
        </span>
        <span className="d-block mx-2">|</span>
        <LinkButton
          titleText="Add/View"
          size="sm"
          className="fw-normal px-0 text-decoration-underline"
          onClick={() => {
            setShowModal(true);
            reInit();
            setTimeout(() => {
              setAnimationClassName('');
              setModalBodyDom(
                document.querySelector(
                  '.comments-modal .modal-body',
                ) as HTMLDivElement,
              );
            }, 0);
          }}
        />
      </div>
      {showModal && (
        <Modal
          show
          customClassName={`m-0 comments-modal ${styles.wizardModal} ${animationClassName} mt-5 mt-sm-0 ${commentStyle.commentsModal}`}
          variant="input"
          onClose={() => {
            setAnimationClassName('hidden');
            setModalBodyDom(undefined);
            setShowModal(false);
          }}
        >
          <div
            className={`wizard-container overflow-hidden ${commentStyle.commentWizard}`}
          >
            <div
              className={`wizard-container-inner ${commentStyle.commentWizardInner}`}
            >
              <div className={commentStyle.mainContainer}>
                <h4 className={commentStyle.headerText}>Activity</h4>
                <div
                  className={`${commentStyle.commentsDataBox} comment-container`}
                >
                  <div
                    className={`d-flex flex-column rounded-3 border p-4 mb-4 comment-form ${commentStyle.commentForm}`}
                  >
                    <div className="text-muted ms-auto mb-2 small">
                      {charCount}/{maxCharCount}
                    </div>
                    <div className="mb-3">
                      <textarea
                        className={`${commentStyle.commentTextArea} txt-comment`}
                        placeholder="Add a comment"
                        maxLength={maxCharCount}
                        value={commentInputValue}
                        onChange={(e: any) => {
                          if (
                            !e.nativeEvent.inputType
                              .toString()
                              .startsWith('delete')
                          ) {
                            if (charCount === maxCharCount) {
                              e.preventDefault();
                              return;
                            }
                          }
                          setCommentInputValue(e.target.value);
                          setShowFormButtons(e.target.value.length > 0);
                        }}
                        disabled={isPosting}
                      />
                    </div>
                    <div
                      className={`text-end ${
                        showFormButtons ? 'd-block' : 'd-none'
                      }`}
                    >
                      <Button
                        titleText="Cancel"
                        variant="light"
                        className="text-primary bg-transparent"
                        size="sm"
                        disabled={isPosting}
                        onClick={() => {
                          setCommentInputValue('');
                          setShowFormButtons(false);
                          setEditCommentSelected(null);
                        }}
                      />
                      <Button
                        titleText="Save Comment"
                        variant="primary"
                        size="sm"
                        className="px-4"
                        disabled={isPosting}
                        // eslint-disable-next-line react/no-unstable-nested-components
                        icon={() =>
                          isPosting ? (
                            <Spinner size="sm" className="me-1" />
                          ) : null
                        }
                        onClick={onPostCommentClick}
                      />
                    </div>
                  </div>
                  <div className="d-block d-sm-flex flex-row align-items-center align-content-center">
                    <div className="flex-grow-0 flex-sm-grow-1">
                      <h4 className={`${commentStyle.headerText} text-start`}>
                        <strong className="me-1 d-block d-sm-inline-block">
                          {deal.dealName}
                        </strong>
                        <small className="fw-normal small  d-block d-sm-inline-block">
                          {deal.id}
                        </small>
                      </h4>
                    </div>
                    <span
                      className={`${commentStyle.text600} d-flex align-items-center align-content-center text-muted mb-3 mb-sm-0`}
                    >
                      <CommentsOutlineIcon
                        fontSize="sm"
                        className={commentStyle.text600}
                      />{' '}
                      Comments: {commentCount}
                    </span>
                  </div>
                  <hr className="mb-4" />
                  <CommentsList
                    accountId={accountId}
                    deal={deal}
                    setEditComment={setEditCommentSelected}
                    setDeleteComment={setDeleteCommentSelected}
                  />
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
